import React from 'react';

import { Trans } from '@lingui/react/macro';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Tooltip,
  Typography,
} from '@mui/material';
import { format, formatDistanceToNow } from 'date-fns';

import { JsonViewer } from '../../../common-components/debug/JsonViewer';
import { VideaTypography } from '../../../common-components/elements/VideaTypography/VideaTypography';
import { useGetDateFNSLocale } from '../../../locales/helpers';
import type { Appointment } from '../../../react-query/useAppointments';
import { useAppointmentsDetails } from '../../../react-query/useAppointmentsDetails';
import type { TFmxSummary } from '../../../react-query/useFmxSummaries';
import type { GetTaskResponse } from '../../../react-query/useGetTasks';
import type { TVisitSummary } from '../../../react-query/useVisitSummaries';
import { useDevtoolsShortcut } from '../../../utils/hooks/useDevtoolsShortcut';

interface Props {
  appointment: Appointment;
  lastSync?: string;
  patientTask?: GetTaskResponse;
  fmxSummary?: TFmxSummary | TVisitSummary;
  isOpen: boolean;
  onClose: () => void;
}

function addHashQueryParam(url: string, key: string, value: string) {
  const existing =
    url.lastIndexOf('?') > url.lastIndexOf('#')
      ? url.substr(url.lastIndexOf('?') + 1)
      : '';

  const query = new URLSearchParams(existing);
  query.set(key, value);
  return `${url.replace(`?${existing}`, '')}?${query.toString()}`;
}

function getShareLink(uaid: string) {
  const url = addHashQueryParam(window.location.href, 'uaid', uaid);
  return url;
}

const InfoDialog: React.FC<Props> = ({
  appointment,
  lastSync,
  patientTask,
  fmxSummary,
  isOpen,
  onClose,
}) => {
  const { showDevtools } = useDevtoolsShortcut();
  const { data: treatmentDetails } = useAppointmentsDetails({
    unifiedIds: [appointment.appointment.unifiedId],
    enabled: isOpen && showDevtools,
  });

  const dateLocale = useGetDateFNSLocale();

  if (!showDevtools) return null;

  return (
    <Dialog
      aria-describedby="alert-dialog-description"
      aria-labelledby="alert-dialog-title"
      fullWidth
      maxWidth="md"
      onClose={onClose}
      open={isOpen}
    >
      <DialogTitle id="alert-dialog-title">Appointment Information</DialogTitle>
      <DialogContent>
        {lastSync && (
          <VideaTypography
            variant="bodyExtrasmall"
            className="date-last-sync"
            component="span"
          >
            <Trans>PMS data updated</Trans>{' '}
            {formatDistanceToNow(new Date(lastSync), {
              locale: dateLocale,
              addSuffix: true,
            }).replace('minutes', 'min')}
            {format(new Date(lastSync), 'h:mmaaa MMM dd, yyyy', {
              locale: dateLocale,
            })}
          </VideaTypography>
        )}
        <Typography variant="body2">
          <Trans>
            Share link:{' '}
            <Link href="#" color="inherit">
              {getShareLink(appointment.appointment.unifiedId)}
            </Link>
          </Trans>
        </Typography>
        {fmxSummary && (
          <>
            <Typography variant="h6">
              <Trans>FMX/Visit Summary JSON</Trans>
            </Typography>
            <JsonViewer src={fmxSummary} />
          </>
        )}
        <Typography variant="h6">
          <Trans>Appointment JSON</Trans>
        </Typography>
        <JsonViewer src={appointment} />
        <>
          <Typography variant="h6">
            <Trans>Tasks JSON</Trans>
          </Typography>
          {patientTask ? (
            <JsonViewer src={patientTask} />
          ) : (
            <Typography>
              <Trans>No tasks to show</Trans>
            </Typography>
          )}
        </>
        <Tooltip title="All treatments for this patient">
          <Typography variant="h6">
            <Trans>Treatments JSON</Trans>
          </Typography>
        </Tooltip>
        {treatmentDetails ? (
          <JsonViewer src={treatmentDetails} />
        ) : (
          <span>
            <Trans>
              no treatments{' '}
              <span role="img" aria-label="disappointed face">
                😔
              </span>
            </Trans>
          </span>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" autoFocus>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InfoDialog;
